import { WhatsAppButtonDefinition, GraphQLWhatsAppHeader } from 'modules/graphql';
import { CallButton, UrlButton, WhatsAppSenderFields } from 'modules/graphql/types';
import { getWhatsAppSendLimitContent } from 'modules/sendingPlatforms/utils/text';
import styled from 'styled-components';
import { BaseOption } from 'ui/selects';
import { spacing } from 'ui/theme';
import { formatPhoneNumber } from 'utils/phoneNumber';
import { SenderStatusBadge } from './formFields/components/SenderStatusBadge';

export const SenderOptionLabel = styled.div`
  display: flex;
  gap: ${spacing.half};
  align-items: center;
`;

type SenderOption = BaseOption<number>;

const defaultButton = {
  buttonText: 'New Button',
  title: 'New Button',
  url: '',
};

const defaultCallButton = {
  buttonText: 'New Button',
  title: 'New Button',
  phoneNumber: '',
};

const defaultQuickReplyButton = {
  buttonText: 'New Button',
  title: 'New Button',
};

export const formatButtonValues = (buttons: WhatsAppButtonDefinition[]) => {
  const formattedVals = [];
  for (let i = 0; i < buttons.length; i += 1) {
    const value = buttons[i];
    const { ['__typename']: omitted, ...remainingButtonObj } = value;
    // @ts-ignore
    if (typeof value?.url === 'string') {
      formattedVals.push({
        urlButton: remainingButtonObj,
      });
      // @ts-ignore
    } else if (typeof value?.phoneNumber === 'string') {
      formattedVals.push({
        callButton: remainingButtonObj,
      });
    } else {
      formattedVals.push({
        quickReplyButton: remainingButtonObj,
      });
    }
  }
  return formattedVals;
};

export const formatHeaderVal = (header?: GraphQLWhatsAppHeader) => {
  if (!header) return;
  const { ['__typename']: omitted, ...remainingHeaderObj } = header;
  if ('text' in header && header.text) return { textHeader: remainingHeaderObj };
  if ('mediaUrl' in header && header.mediaUrl) return { imageHeader: remainingHeaderObj };
  return;
};

export const selectDefaultButton = (buttonCounts: Record<string, number>) => {
  if (buttonCounts.url < 2) {
    return defaultButton;
  } else if (buttonCounts.phoneNumber < 1) {
    return defaultCallButton;
  }
  return defaultQuickReplyButton;
};

export const getButtonCounts = (buttonList: WhatsAppButtonDefinition[]) =>
  buttonList.reduce(
    (acc, curr) => {
      if (Object.prototype.hasOwnProperty.call(curr as UrlButton, 'url')) {
        return { ...acc, url: acc.url + 1 };
      } else if (Object.prototype.hasOwnProperty.call(curr as CallButton, 'phoneNumber')) {
        return { ...acc, phoneNumber: acc.phoneNumber + 1 };
      }
      return acc;
    },
    { url: 0, phoneNumber: 0 },
  );

export const formatWhatsAppSender = (
  whatsAppSenders: WhatsAppSenderFields[],
  isWhatsAppGAEnabled: boolean,
) =>
  whatsAppSenders.reduce(
    (acc, { id, displayName, phoneNumber, qualityRating, sendLimit, status }) => {
      const senderPhoneNumber = formatPhoneNumber(phoneNumber);
      const label = (
        <SenderOptionLabel key={id} data-test="sender-option-label">
          {displayName || senderPhoneNumber}
          {isWhatsAppGAEnabled && (
            <SenderStatusBadge
              data-test="sender-status-badge-select-option"
              sendLimit={sendLimit}
              status={status}
            />
          )}
        </SenderOptionLabel>
      );
      const doubleLine = `${displayName ? senderPhoneNumber.concat(' • ') : ''}${qualityRating} quality • ${getWhatsAppSendLimitContent(sendLimit)}`;
      return id
        ? [
            ...acc,
            {
              id: id.toString(),
              label,
              ...(isWhatsAppGAEnabled ? { doubleLine } : {}),
              value: id,
            },
          ]
        : acc;
    },
    [] as SenderOption[],
  );

export const getSelectedSenderLabel = (
  senders: WhatsAppSenderFields[],
  id?: number,
  fallbackText?: string,
) => {
  const selectedSender = senders.find((sender) => sender?.id === id);
  return (
    selectedSender?.displayName ||
    formatPhoneNumber(selectedSender?.phoneNumber || '') ||
    fallbackText ||
    ''
  );
};
